import { useEffect, useState } from "react";
import { collection, getDocs, orderBy, query } from "firebase/firestore";

import { db } from "../../../../config/firebase";
import { createBookingConfirmationPdf } from "../../../../utils/bookingConfirmation";
import { downloadFilePayload } from "../../../../utils/download";
import "./BookingsList.css";

function BookingsList() {
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadState, setDownloadState] = useState({});

  useEffect(() => {
    let isMounted = true;

    const fetchBookings = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const bookingQuery = query(
          collection(db, "bookings"),
          orderBy("date", "asc")
        );
        const querySnapshot = await getDocs(bookingQuery);

        if (!isMounted) {
          return;
        }

        setBookings(
          querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
        );
      } catch (fetchError) {
        if (!isMounted) {
          return;
        }

        console.error("Error Fetching Bookings:", fetchError);
        setError("Unable to load bookings. Please try again.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchBookings();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDownloadDocument = async (booking) => {
    if (!booking?.id) {
      return;
    }

    const bookingId = booking.id;
    setDownloadState((previous) => ({ ...previous, [bookingId]: true }));

    try {
      const documentDetails = {
        guestName: booking.name,
        bookingDateIso: booking.date,
        pickupPointDisplay: booking.pickupPoint,
        totalPriceValue:
          typeof booking.totalPrice === "number"
            ? booking.totalPrice
            : Number(booking.totalPrice),
      };

      const pdf = await createBookingConfirmationPdf(documentDetails);

      downloadFilePayload({
        blob: pdf?.blob,
        base64: pdf?.base64,
        fileName: pdf?.fileName,
        mimeType: "application/pdf",
      });
    } catch (downloadError) {
      console.error(
        "Failed to download booking confirmation PDF",
        downloadError
      );
    } finally {
      setDownloadState((previous) => {
        const nextState = { ...previous };
        delete nextState[bookingId];
        return nextState;
      });
    }
  };

  return (
    <div className="bookingsListContainer">
      <div className="bookingsListHeader">
        <h2>Bookings</h2>
        <p className="bookingsListSubhead">
          Monitor upcoming sessions and guest details in one place.
        </p>
      </div>

      {isLoading && (
        <div className="bookingsLoadingState" role="status" aria-live="polite">
          <span className="bookingsSpinner" aria-hidden="true" />
          <span className="bookingsLoadingMessage">Loading bookings…</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="bookingsErrorState" role="alert">
          {error}
        </div>
      )}

      {!isLoading && !error && bookings.length === 0 && (
        <div className="bookingsEmptyState">
          <h3>No bookings yet</h3>
          <p>
            When guests reserve a session their information will appear here for
            your team to review.
          </p>
        </div>
      )}

      {!isLoading && !error && bookings.length > 0 && (
        <div className="bookingsTableWrapper">
          <table className="bookingsTable">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Session</th>
                <th scope="col">Pickup point</th>
                <th scope="col">Guests</th>
                <th scope="col">Guest name</th>
                <th scope="col">Email</th>
                <th scope="col">Documents</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr key={booking.id}>
                  <td data-title="Date">{formatDate(booking.date)}</td>
                  <td data-title="Session">{booking.session || "—"}</td>
                  <td data-title="Pickup point">{booking.pickupPoint || "—"}</td>
                  <td data-title="Guests">{booking.people ?? "—"}</td>
                  <td data-title="Guest name">{booking.name || "—"}</td>
                  <td data-title="Email">{booking.email || "—"}</td>
                  <td data-title="Documents">
                    <button
                      type="button"
                      className="bookingDocumentButton"
                      onClick={() => handleDownloadDocument(booking)}
                      disabled={Boolean(downloadState[booking.id])}
                    >
                      {downloadState[booking.id]
                        ? "Preparing…"
                        : "Download PDF"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatDate(dateString) {
  if (!dateString) {
    return "—";
  }

  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
  }

  return "—";
}

export default BookingsList;
